import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import {
  DatasetKindIcon,
  nameGlobMatches,
  useDatasetNames,
} from '@/features/catalog-browser';
import { useImpact, useNodeByDataset } from '@/features/lineage';
import { severityTone, useBindings, useRules } from '@/features/rules';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TextField } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import {
  useClearExpected,
  useEvaluateDataset,
  useFinding,
  useMarkExpected,
  useMuteSeries,
  useRaiseIncident,
  useSetThreshold,
  useUnmuteSeries,
} from '../api/anomaly';
import type {
  DetectorInfo,
  FindingDetail,
  Irregularity,
  SeriesRef,
} from '../api/anomaly';
import {
  bannerTone,
  baselineFooter,
  basisKey,
  checkKey,
  expectedPhrase,
  measureValue,
  primaryAction,
  sensitivityLabel,
  signalKey,
  verdictKey,
  verdictTone,
  windowLabel,
} from '../model/presentation';
import { SeriesChart } from './SeriesChart';

/* One anomaly finding: what the statistics decided, the series behind it, the detector, and what a
 * reader can do about it.
 *
 * The verdict drives the page. An `anomalous` verdict gets a danger-toned banner and a "Raise an
 * incident" primary action; every other verdict is silence, so the primary becomes "View
 * executions" — the useful next thing is to look at the rules that run on this table. Mute /
 * mark-expected / edit-detector are standing decisions about the *series*, not this one finding, and
 * say so.
 *
 * **A point-in-time finding gets a different page, not a degraded one.** It has no series, so there
 * is no chart; no detector decided it, so there is no detector card; and it watches no series, so
 * mute / mark-expected / edit-detector have nothing to act on. What it does have — the column, the
 * check, the measurement and the bar it failed — is shown in their place, with "historical
 * comparison: not available" said in words rather than left to be inferred from a missing chart.
 */

function DefRow({ term, children }: { term: string; children: ReactNode }): ReactElement {
  return (
    <div className="lq-defrow">
      <span className="lq-defrow__term">{term}</span>
      <span>{children}</span>
    </div>
  );
}

function DetectorCard({ detector }: { detector: DetectorInfo }): ReactElement {
  const { t } = useTranslation();
  const sensitivity = sensitivityLabel(detector);
  return (
    <Card
      title={t('anomaly.detail.detector.title')}
      help={t('help.term.anomalyDetector')}
    >
      <DefRow term={t('anomaly.detail.detector.method')}>{detector.method}</DefRow>
      <DefRow term={t('anomaly.detail.detector.window')}>{windowLabel(detector)}</DefRow>
      <DefRow term={t('anomaly.detail.detector.sensitivity')}>
        <span className="lq-mono">{sensitivity.value}</span>
        {sensitivity.overridden && (
          <span className="lq-dim"> {t('anomaly.detail.detector.overridden')}</span>
        )}
      </DefRow>
    </Card>
  );
}

function ChartCard({ detail }: { detail: FindingDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Card title={t('anomaly.detail.chart.title')} help={t('help.term.baseline')}>
      {detail.series.length < 2 ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('anomaly.detail.chart.tooFew', { count: detail.series.length })}
        </p>
      ) : (
        <>
          <SeriesChart
            points={detail.series}
            band={detail.baseline_band}
            label={t('anomaly.detail.chart.label')}
          />
          <div className="lq-serieschart__legend">
            <span className="lq-serieschart__key">
              <span className="lq-serieschart__swatch lq-serieschart__swatch--line" />
              {t('anomaly.detail.chart.observed')}
            </span>
            {detail.baseline_band !== null && (
              <span className="lq-serieschart__key">
                <span className="lq-serieschart__swatch lq-serieschart__swatch--band" />
                {t('anomaly.detail.chart.expected')}
              </span>
            )}
          </div>
          <div className="lq-serieschart__foot">
            <span>
              {t('anomaly.detail.chart.periods', { count: detail.series.length })}
            </span>
            <span>{baselineFooter(detail)}</span>
            <span>{t('anomaly.detail.chart.now')}</span>
          </div>
        </>
      )}
    </Card>
  );
}

/* The downstream half of "what this affects". Its own component so `useImpact` only mounts once a
   lineage node has resolved — a query keyed on an empty id would hit `/nodes//impact`. */
function DownstreamList({ nodeId }: { nodeId: string }): ReactElement {
  const { t } = useTranslation();
  const impact = useImpact(nodeId, null);

  if (impact.isPending) return <SkeletonRows rows={2} columns={2} />;

  const noDownstream = (
    <p className="lq-muted" style={{ margin: 0 }}>
      {t('anomaly.detail.affects.noDownstream')}
    </p>
  );
  if (impact.isError) return noDownstream;

  const { affected: downstream, complete } = impact.data;
  if (downstream.length === 0) return noDownstream;

  return (
    <ul className="lq-plainlist">
      {downstream.map((affected) => (
        <li key={affected.node_id}>
          <span className="lq-mono">{affected.display_name}</span>{' '}
          <span className="lq-dim">
            {t(`anomaly.detail.affects.nodeKind.${affected.kind}`)} ·{' '}
            {t('anomaly.detail.affects.hops', { count: affected.depth })}
          </span>
        </li>
      ))}
      {!complete && (
        <li className="lq-dim">{t('anomaly.detail.affects.impactIncomplete')}</li>
      )}
    </ul>
  );
}

function AffectsCard({
  datasetId,
  qualified,
}: {
  datasetId: string;
  qualified: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const bindings = useBindings();
  const rules = useRules('');
  const node = useNodeByDataset(datasetId);

  const rulesById = new Map((rules.data?.items ?? []).map((rule) => [rule.id, rule]));
  const boundRules = (bindings.data?.items ?? []).filter((binding) =>
    binding.selectors.some(
      (selector) =>
        selector.kind === 'name_glob' &&
        qualified !== null &&
        typeof selector.pattern === 'string' &&
        nameGlobMatches(selector.pattern, qualified),
    ),
  );

  return (
    <Card
      title={t('anomaly.detail.affects.title')}
      hint={t('anomaly.detail.affects.hint')}
    >
      <h3 className="lq-subhead">{t('anomaly.detail.affects.rules')}</h3>
      {bindings.isPending ? (
        <SkeletonRows rows={2} columns={2} />
      ) : boundRules.length === 0 ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('anomaly.detail.affects.noRules')}
        </p>
      ) : (
        <DataTable
          caption={t('anomaly.detail.affects.rulesCaption')}
          columns={[
            { key: 'rule', label: t('anomaly.detail.affects.column.rule') },
            { key: 'kind', label: t('anomaly.detail.affects.column.kind') },
            { key: 'severity', label: t('anomaly.detail.affects.column.severity') },
          ]}
        >
          {boundRules.map((binding) => {
            const rule = rulesById.get(binding.rule_id);
            return (
              <tr key={binding.id}>
                <td>
                  {rule === undefined ? (
                    <span className="lq-dim">
                      {t('anomaly.detail.affects.unresolvedRule')}
                    </span>
                  ) : (
                    <Link to={`/rules/${encodeURIComponent(rule.id)}`}>{rule.name}</Link>
                  )}
                </td>
                <td className="lq-muted">
                  {rule === undefined ? '—' : t(`rules.kind.${rule.kind}`)}
                </td>
                <td>
                  <StatusBadge
                    tone={severityTone(binding.severity)}
                    label={t(`rules.severity.${binding.severity}.label`)}
                    title={t(`rules.severity.${binding.severity}.meaning`)}
                  />
                </td>
              </tr>
            );
          })}
        </DataTable>
      )}

      <h3 className="lq-subhead" style={{ marginTop: 'var(--space-5)' }}>
        {t('anomaly.detail.affects.downstream')}
      </h3>
      {node.isPending ? (
        <SkeletonRows rows={2} columns={2} />
      ) : node.isError ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('anomaly.detail.affects.noLineage')}
        </p>
      ) : (
        <DownstreamList nodeId={node.data.id} />
      )}
    </Card>
  );
}

function DetectorModal({
  series: seriesRef,
  currentOverride,
  onClose,
}: {
  series: SeriesRef;
  currentOverride: number | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const setThreshold = useSetThreshold();
  const evaluate = useEvaluateDataset();
  const [value, setValue] = useState(
    currentOverride === null ? '' : String(currentOverride),
  );

  const parsed = value.trim() === '' ? null : Number(value);
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed <= 0);

  return (
    <Modal open title={t('anomaly.detail.editDetector.title')} onClose={onClose}>
      <p className="lq-muted">{t('anomaly.detail.editDetector.body')}</p>
      <TextField
        label={t('anomaly.detail.editDetector.field')}
        type="number"
        min={0}
        value={value}
        onChange={setValue}
        hint={t('anomaly.detail.editDetector.fieldHint')}
      />
      {setThreshold.isError && <Alert tone="error">{setThreshold.error.message}</Alert>}
      {evaluate.isError && <Alert tone="error">{evaluate.error.message}</Alert>}
      <div className="lq-form-actions">
        <Button
          tone="primary"
          busy={setThreshold.isPending}
          disabled={invalid}
          onClick={() => {
            setThreshold.mutate(
              { ...seriesRef, override: parsed },
              {
                onSuccess: () => {
                  onClose();
                },
              },
            );
          }}
        >
          {t('anomaly.detail.editDetector.save')}
        </Button>
        <Button
          busy={evaluate.isPending}
          onClick={() => {
            evaluate.mutate(seriesRef.datasetId);
          }}
        >
          {t('anomaly.detail.editDetector.reevaluate')}
        </Button>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </div>
      {evaluate.isSuccess && (
        <p className="lq-muted" style={{ marginBottom: 0 }}>
          {t('anomaly.detail.editDetector.reevaluated')}
        </p>
      )}
    </Modal>
  );
}

/** One point-in-time irregularity. Every figure here is the current profile's own, and the one
 * thing a historical finding would show that this cannot — a comparison with the past — is named as
 * unavailable rather than drawn as an empty chart a reader might take for "no change". */
function IrregularityDetail({
  detail,
  irregularity,
}: {
  detail: FindingDetail;
  irregularity: Irregularity;
}): ReactElement {
  const { t } = useTranslation();
  const names = useDatasetNames();
  const datasetName = names.data?.byId.get(detail.dataset_id) ?? detail.dataset_id;
  const qualified = names.data?.byId.get(detail.dataset_id) ?? null;
  const measuredAt = new Date(irregularity.measured_at);

  return (
    <section aria-labelledby="anomaly-heading">
      <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
        <Link to={SCREEN_PATHS.anomalies}>{t('anomaly.title')}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <span aria-current="page">{datasetName}</span>
      </nav>

      <div className="lq-page-header">
        <div>
          <div className="lq-otype-title">
            <DatasetKindIcon datasetId={detail.dataset_id} />
            <h2 id="anomaly-heading" className="lq-page-header__title lq-mono">
              {datasetName}
            </h2>
          </div>
          <p className="lq-page-header__lede">
            {[
              irregularity.column,
              t(checkKey(irregularity.check)),
              t(basisKey(detail.basis)),
            ].join(' · ')}
          </p>
        </div>
        <div className="lq-page-header__actions">
          <StatusBadge
            tone={verdictTone(detail.verdict)}
            label={t(verdictKey(detail.verdict))}
            title={t(`${verdictKey(detail.verdict)}.meaning`)}
          />
        </div>
      </div>

      <Alert tone={bannerTone(detail.verdict)}>{detail.explanation.text}</Alert>

      <div className="lq-toolbar" style={{ marginTop: 'var(--space-4)' }}>
        <Link
          className="lq-btn lq-btn--primary"
          to={`/datasets/${encodeURIComponent(detail.dataset_id)}`}
        >
          {t('anomaly.detail.viewDataset')}
        </Link>
      </div>

      <div className="lq-stack" style={{ marginTop: 'var(--space-4)' }}>
        <div className="lq-grid">
          <Card
            title={t('anomaly.detail.irregularity.title')}
            hint={t('anomaly.detail.irregularity.hint')}
          >
            <DefRow term={t('anomaly.detail.irregularity.dataset')}>
              <span className="lq-mono">{datasetName}</span>
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.column')}>
              <span className="lq-mono">{irregularity.column}</span>
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.check')}>
              {t(checkKey(irregularity.check))}
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.current')}>
              <span className="lq-mono">
                {measureValue(irregularity, irregularity.observed)}
              </span>{' '}
              <span className="lq-dim">
                {t(`anomaly.measure.${irregularity.measure}`)}
              </span>
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.expected')}>
              <span className="lq-mono">{expectedPhrase(irregularity)}</span>
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.severity')}>
              {detail.critical
                ? t('anomaly.detail.irregularity.critical')
                : t('anomaly.detail.irregularity.warning')}
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.history')}>
              {t('anomaly.detail.irregularity.historyUnavailable')}
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.source')}>
              {t(`anomaly.source.${irregularity.source}`)}
            </DefRow>
            <DefRow term={t('anomaly.detail.irregularity.measuredAt')}>
              {Number.isNaN(measuredAt.getTime())
                ? irregularity.measured_at
                : measuredAt.toLocaleString()}
            </DefRow>
          </Card>
          <AffectsCard datasetId={detail.dataset_id} qualified={qualified} />
        </div>
      </div>
    </section>
  );
}

export function AnomalyDetail(): ReactElement {
  const { t } = useTranslation();
  const { findingId = '' } = useParams<{ findingId: string }>();
  const finding = useFinding(findingId);
  const names = useDatasetNames();
  const raiseIncident = useRaiseIncident();
  const mute = useMuteSeries();
  const unmute = useUnmuteSeries();
  const markExpected = useMarkExpected();
  const clearExpected = useClearExpected();
  const [editingDetector, setEditingDetector] = useState(false);

  if (finding.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('anomaly.detail.loading')}
        </p>
        <SkeletonRows rows={4} columns={2} />
      </>
    );
  }

  if (finding.isError) {
    return (
      <>
        <Alert tone="error" title={t('anomaly.detail.unreachable')}>
          {finding.error.message}
        </Alert>
        <p style={{ marginTop: 'var(--space-3)' }}>
          <Link to={SCREEN_PATHS.anomalies}>{t('anomaly.detail.back')}</Link>
        </p>
      </>
    );
  }

  const detail = finding.data;
  if (detail.irregularity != null) {
    return <IrregularityDetail detail={detail} irregularity={detail.irregularity} />;
  }
  const { detector, signal } = detail;
  if (detector === null || signal === null) {
    /* A historical finding always has both. A row with neither is malformed, and saying so beats
       drawing a chart for a series the payload does not name. */
    return (
      <>
        <Alert tone="error" title={t('anomaly.detail.unreachable')}>
          {t('anomaly.detail.malformed')}
        </Alert>
        <p style={{ marginTop: 'var(--space-3)' }}>
          <Link to={SCREEN_PATHS.anomalies}>{t('anomaly.detail.back')}</Link>
        </p>
      </>
    );
  }
  const seriesRef: SeriesRef = { datasetId: detail.dataset_id, signal };
  const datasetName = names.data?.byId.get(detail.dataset_id) ?? detail.dataset_id;
  const qualified = names.data?.byId.get(detail.dataset_id) ?? null;
  const state = detail.series_state;
  const action = primaryAction(detail.verdict);
  const incidentId = detail.incident_id ?? raiseIncident.data?.incident_id ?? null;

  return (
    <section aria-labelledby="anomaly-heading">
      <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
        <Link to={SCREEN_PATHS.anomalies}>{t('anomaly.title')}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <span aria-current="page">{datasetName}</span>
      </nav>

      <div className="lq-page-header">
        <div>
          <div className="lq-otype-title">
            <DatasetKindIcon datasetId={detail.dataset_id} />
            <h2 id="anomaly-heading" className="lq-page-header__title lq-mono">
              {datasetName}
            </h2>
          </div>
          <p className="lq-page-header__lede">
            {[t(signalKey(signal)), detector.method, windowLabel(detector)].join(' · ')}
          </p>
        </div>
        <div className="lq-page-header__actions">
          <StatusBadge
            tone={verdictTone(detail.verdict)}
            label={t(verdictKey(detail.verdict))}
            title={t(`${verdictKey(detail.verdict)}.meaning`)}
          />
        </div>
      </div>

      <Alert tone={bannerTone(detail.verdict)}>{detail.explanation.text}</Alert>

      <div className="lq-toolbar" style={{ marginTop: 'var(--space-4)' }}>
        {action === 'raise-incident' ? (
          incidentId !== null ? (
            <Link className="lq-btn" to={SCREEN_PATHS.incidents}>
              {t('anomaly.detail.incidentOpen')}
            </Link>
          ) : (
            <Button
              tone="primary"
              busy={raiseIncident.isPending}
              onClick={() => {
                raiseIncident.mutate(findingId);
              }}
            >
              {t('anomaly.detail.raiseIncident')}
            </Button>
          )
        ) : action === 'view-dataset' ? (
          <Link
            className="lq-btn lq-btn--primary"
            to={`/datasets/${encodeURIComponent(detail.dataset_id)}`}
          >
            {t('anomaly.detail.viewDataset')}
          </Link>
        ) : (
          <Link className="lq-btn lq-btn--primary" to={SCREEN_PATHS.executions}>
            {t('anomaly.detail.viewExecutions')}
          </Link>
        )}

        <Button
          busy={markExpected.isPending || clearExpected.isPending}
          onClick={() => {
            if (state.expected) clearExpected.mutate(seriesRef);
            else markExpected.mutate(seriesRef);
          }}
        >
          {state.expected
            ? t('anomaly.detail.clearExpected')
            : t('anomaly.detail.markExpected')}
        </Button>

        <Button
          busy={mute.isPending || unmute.isPending}
          onClick={() => {
            if (state.muted) unmute.mutate(seriesRef);
            else mute.mutate(seriesRef);
          }}
        >
          {state.muted ? t('anomaly.detail.unmute') : t('anomaly.detail.mute')}
        </Button>

        <Button
          onClick={() => {
            setEditingDetector(true);
          }}
        >
          {t('anomaly.detail.editDetector.open')}
        </Button>
      </div>

      {(raiseIncident.isError || mute.isError || unmute.isError) && (
        <Alert tone="error">
          {(raiseIncident.error ?? mute.error ?? unmute.error)?.message}
        </Alert>
      )}

      {state.muted && <p className="lq-muted">{t('anomaly.detail.mutedNote')}</p>}
      {state.expected && <p className="lq-muted">{t('anomaly.detail.expectedNote')}</p>}

      <div className="lq-stack" style={{ marginTop: 'var(--space-4)' }}>
        <ChartCard detail={detail} />
        <div className="lq-grid">
          <DetectorCard detector={detector} />
          {detail.verdict === 'insufficient_history' ? (
            <Card title={t('anomaly.detail.affects.title')}>
              <EmptyState
                title={t('anomaly.detail.affects.coldStartTitle')}
                guidance={t('anomaly.detail.affects.coldStartGuidance')}
              />
            </Card>
          ) : (
            <AffectsCard datasetId={detail.dataset_id} qualified={qualified} />
          )}
        </div>
      </div>

      {editingDetector && (
        <DetectorModal
          series={seriesRef}
          currentOverride={state.threshold_override}
          onClose={() => {
            setEditingDetector(false);
          }}
        />
      )}
    </section>
  );
}
